import React, { useState, useEffect, useCallback, useMemo } from 'react'
import { useHistory, useParams } from 'react-router-dom'

import { 
  Button, 
  Form, 
  Input,
  Panel, 
  SelectPicker, 
  Toggle, 
  ButtonToolbar,
  Table,
  Modal,
  FlexboxGrid,
  IconButton,
  Divider,
  Nav,
  Navbar,
  Badge,
  Tag
} from 'rsuite'

import { AddOutline, CreateOutline, TrashOutline, MenuOutline, ChevronUpOutline, ChevronDownOutline } from 'react-ionicons'

import SettingsTopMenu from '@components/settings/SettingsTopMenu'
import GraphQLCommonErrorDisplay from '@components/common/GraphQLCommonErrorDisplay'
import FormCheckbox from '@components/common/form/FormCheckbox'
import DropzonePreview from '@components/common/form/DropzonePreview'
import DropzoneUploader from '@components/common/form/DropzoneUploader'
import SelectLocale from '@components/common/localizedForm/SelectLocale'
import LocalizedForm from '@components/common/localizedForm/LoclalizedForm'

import { API_URL } from '@@partner:config/api'

// Constants for menu item image upload
const API_URL_UPLOAD_MENU_ITEM_IMAGE = `${API_URL}/menu/item/image/upload`
const UPLOAD_NAME_MENU_ITEM_IMAGE = 'menu-item-image'

import type { Menu, MenuItem, MenuItemTranslation } from '@@partner:types:app'
import { MenuType, LinkType } from '@@partner:types:app'

import { ROUTE_SETTINGS_MENUS } from '@@partner:config/routes'

import { useFetchSingleMenu } from '@@partner:graphql/query/menu/singleMenu.gql'
import { useCreateMenu } from '@@partner:graphql/mutation/menu/createMenu.gql'
import { useUpdateMenu } from '@@partner:graphql/mutation/menu/updateMenu.gql'
import { useCreateMenuItem } from '@@partner:graphql/mutation/menu/createMenuItem.gql'
import { useUpdateMenuItem } from '@@partner:graphql/mutation/menu/updateMenuItem.gql'
import { useDeleteMenuItem } from '@@partner:graphql/mutation/menu/deleteMenuItem.gql'
import { GQL_ID } from '@@partner:types:graphql'

// Временные типы для форм
interface MenuFormData {
  name: string
  code: string
  type: MenuType
  is_active: boolean
}

interface MenuItemFormData {
  internal_name: string
  title: string
  image_file_id?: GQL_ID | null
  image_url?: string
  text_color: string
  link_url: string
  link_type: LinkType
  bold: boolean
  is_active: boolean
  translations: MenuItemTranslation[]
}

interface MenuEditPageParams {
  menuId?: string
}

const MENU_TYPES = [
  { label: 'Заголовок', value: MenuType.header },
  { label: 'Футер', value: MenuType.footer },
  { label: 'Боковое меню', value: MenuType.sidebar },
  { label: 'Мобильное меню', value: MenuType.mobile }
]

const LINK_TYPES = [
  { label: 'Внутренняя ссылка', value: LinkType.internal },
  { label: 'Внешняя ссылка', value: LinkType.external },
  { label: 'Категория', value: LinkType.category },
  { label: 'Товар', value: LinkType.product }
]

const DEFAULT_MENU_ITEM: MenuItemFormData = {
  internal_name: '',
  title: '',
  image_file_id: null,
  image_url: '',
  text_color: '#333333',
  link_url: '',
  link_type: LinkType.internal,
  bold: false,
  is_active: true,
  translations: []
}

const { Column, HeaderCell, Cell } = Table

export default function MenuEditPage () {
  const history = useHistory()
  const { menuId } = useParams<MenuEditPageParams>()
  const isEditing = !!menuId

  // GraphQL хуки
  const { data: menuData, loading: menuLoading, error: menuLoadError } = useFetchSingleMenu(
    { id: menuId! },
    { skip: !isEditing || !menuId }
  )

  console.log(menuData)
  const [ createMenu ] = useCreateMenu()
  const [ updateMenu ] = useUpdateMenu()
  const [ createMenuItem ] = useCreateMenuItem()
  const [ updateMenuItem ] = useUpdateMenuItem()
  const [ deleteMenuItem ] = useDeleteMenuItem()

  const [ formData, setFormData ] = useState<MenuFormData>({
    name: '',
    code: '',
    type: MenuType.header,
    is_active: true
  })

  const [ menuItems, setMenuItems ] = useState<MenuItem[]>([])

  const [ loading, setLoading ] = useState(false)
  const [ error, setError ] = useState<Error | null>(null)
  
  // Состояние для вкладок
  const [ activeTab, setActiveTab ] = useState(isEditing ? 'items' : 'settings')
  
  // Состояние для модального окна элементов меню
  const [ showItemModal, setShowItemModal ] = useState(false)
  const [ editingItem, setEditingItem ] = useState<MenuItem | null>(null)
  const [ itemFormData, setItemFormData ] = useState<MenuItemFormData>(DEFAULT_MENU_ITEM)
  const [ selectedItemLanguage, setSelectedItemLanguage ] = useState('')

  const itemLanguages = useMemo(() => {
    const uniqueCodes = new Set(
      (itemFormData.translations || [])
        .map(translation => translation.language_code)
        .filter(Boolean)
    )

    return [ ...uniqueCodes ]
  }, [ itemFormData.translations ])

  const selectedItemTranslationTitle = selectedItemLanguage
    ? itemFormData.translations.find(translation => translation.language_code === selectedItemLanguage)?.title || ''
    : ''

  // Загрузка данных при редактировании
  useEffect(() => {
    if (menuData?.singleMenu) {
      const menu = menuData.singleMenu
      setFormData({
        name: menu.name,
        code: menu.code,
        type: menu.type,
        is_active: menu.is_active
      })
      
      // Если есть элементы меню, загружаем их
      if (menu.menuItems) {
        setMenuItems(menu.menuItems)
      }
    }
  }, [ menuData ])

  const handleSubmit = async () => {
    setLoading(true)
    setError(null)
    
    try {
      if (isEditing && menuId) {
        // Обновление существующего меню
        await updateMenu({
          variables: {
            id: menuId,
            input: {
              name: formData.name,
              code: formData.code,
              type: formData.type,
              is_active: formData.is_active
            }
          }
        })
      }
      else {
        // Создание нового меню
        await createMenu({
          variables: {
            input: {
              name: formData.name,
              code: formData.code,
              type: formData.type,
              is_active: formData.is_active
            }
          }
        })
      }
      
      // После успешного сохранения перенаправляем на список
      history.push(ROUTE_SETTINGS_MENUS)
    }
    catch (err) {
      setError(err as Error)
    }
    finally {
      setLoading(false)
    }
  }

  const handleCancel = () => {
    history.push(ROUTE_SETTINGS_MENUS)
  }

  const updateFormData = (field: keyof MenuFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }))
  }

  const updateItemFormData = (field: keyof MenuItemFormData, value: any) => {
    setItemFormData(prev => ({ ...prev, [field]: value }))
  }

  // Handler for menu item image upload
  const handleMenuItemImageUploaded = useCallback(
    (image_file_id: GQL_ID, url: string) => {
      setItemFormData(prev => ({
        ...prev,
        image_file_id,
        image_url: url
      }))
    },
    []
  )

  // Handler for removing menu item image
  const handleUnlinkMenuItemImage = useCallback(() => {
    setItemFormData(prev => ({
      ...prev,
      image_file_id: '',
      image_url: ''
    }))
  }, [])

  // Функции для управления элементами меню
  const handleAddItem = () => {
    setEditingItem(null)
    setItemFormData(DEFAULT_MENU_ITEM)
    setSelectedItemLanguage('')
    setShowItemModal(true)
  }

  const handleEditItem = (item: MenuItem) => {
    setEditingItem(item)
    setItemFormData({
      internal_name: item.internal_name,
      title: item.title,
      image_file_id: item.image_file_id || '',
      image_url: item.image?.url || '',
      text_color: item.text_color || '#333333',
      link_url: item.link_url || '',
      link_type: item.link_type,
      bold: item.bold || false,
      is_active: item.is_active,
      translations: item.translations || []
    })
    setSelectedItemLanguage(item.translations?.[0]?.language_code || '')
    setShowItemModal(true)
  }

  const handleAddItemLanguage = (language: string) => {
    const hasLanguage = itemFormData.translations.some(translation => translation.language_code === language)
    if (hasLanguage) {
      setSelectedItemLanguage(language)
      return
    }

    setItemFormData(prev => ({
      ...prev,
      translations: [ ...prev.translations, { language_code: language, title: '' } ]
    }))
    setSelectedItemLanguage(language)
  }

  const handleRemoveItemLanguage = (language: string) => {
    const nextTranslations = itemFormData.translations.filter(translation => translation.language_code !== language)
    setItemFormData(prev => ({
      ...prev,
      translations: nextTranslations
    }))

    if (selectedItemLanguage === language) {
      setSelectedItemLanguage(nextTranslations[0]?.language_code || '')
    }
  }

  const handleItemTranslationChange = (title: string) => {
    if (!selectedItemLanguage) {
      return
    }

    const existingIndex = itemFormData.translations.findIndex(
      translation => translation.language_code === selectedItemLanguage
    )

    if (existingIndex >= 0) {
      const nextTranslations = [ ...itemFormData.translations ]
      nextTranslations[existingIndex] = {
        ...nextTranslations[existingIndex],
        title
      }

      setItemFormData(prev => ({
        ...prev,
        translations: nextTranslations
      }))
      return
    }

    setItemFormData(prev => ({
      ...prev,
      translations: [ ...prev.translations, { language_code: selectedItemLanguage, title } ]
    }))
  }

  const handleDeleteItem = async (itemId: string | number) => {
    try {
      await deleteMenuItem({
        variables: {
          id: itemId.toString()
        }
      })
      
      // Обновляем локальное состояние
      setMenuItems(prev => prev.filter(item => item.id !== itemId.toString()))
    }
    catch (error) {
      console.error('Error deleting menu item:', error)
      alert('Ошибка при удалении элемента меню')
    }
  }

  const handleSaveItem = async () => {
    if (!menuId && !isEditing) {
      // Если создаем новое меню, сначала нужно сохранить само меню
      alert('Сначала сохраните меню, а затем добавляйте элементы')
      return
    }

    try {
      if (editingItem) {
        // Редактирование существующего элемента
        const result = await updateMenuItem({
          variables: {
            id: editingItem.id,
            input: {
              internal_name: itemFormData.internal_name,
              title: itemFormData.title,
              image_file_id: itemFormData.image_file_id && ((typeof itemFormData.image_file_id === 'string' && itemFormData.image_file_id.length > 0) || typeof itemFormData.image_file_id === 'number') ? itemFormData.image_file_id : null,
              text_color: itemFormData.text_color,
              link_url: itemFormData.link_url,
              link_type: itemFormData.link_type as LinkType,
              bold: itemFormData.bold,
              is_active: itemFormData.is_active,
              ...(itemFormData.translations.length > 0 && {
                i18n: itemFormData.translations.filter(t => t.language_code && t.title)
              })
            }
          }
        })

        // Обновляем локальное состояние
        if (result.data?.updateMenuItem) {
          setMenuItems(prev => prev.map(item => 
            item.id === editingItem.id ? result.data!.updateMenuItem : item
          ))
        }
      }
      else {
        
        const result = await createMenuItem({
          variables: {
            input: {
              menu_id: menuId!,
              internal_name: itemFormData.internal_name,
              title: itemFormData.title,
              image_file_id: itemFormData.image_file_id,
              text_color: itemFormData.text_color,
              link_url: itemFormData.link_url,
              link_type: itemFormData.link_type as LinkType,
              bold: itemFormData.bold,
              is_active: itemFormData.is_active,
              sort_order: menuItems.length + 1,
              ...(itemFormData.translations.length > 0 && {
                i18n: itemFormData.translations.filter(t => t.language_code && t.title)
              })
            }
          }
        })

        // Обновляем локальное состояние
        if (result.data?.createMenuItem) {
          setMenuItems(prev => [ ...prev, result.data!.createMenuItem ])
        }
      }
      
      setShowItemModal(false)
      setEditingItem(null)
      setItemFormData(DEFAULT_MENU_ITEM)
      setSelectedItemLanguage('')
    }
    catch (error) {
      console.error('Error saving menu item:', error)
      alert('Ошибка при сохранении элемента меню')
    }
  }

  const moveItem = (fromIndex: number, toIndex: number) => {
    const items = [ ...menuItems ]
    const [ removed ] = items.splice(fromIndex, 1)
    items.splice(toIndex, 0, removed)
    
    // Обновляем sort_order
    const updatedItems = items.map((item, index) => ({
      ...item,
      sort_order: index + 1
    }))
    
    setMenuItems(updatedItems)
  }

  const moveItemUp = (itemId: GQL_ID) => {
    const sortedItems = [ ...menuItems ].sort((a, b) => a.sort_order - b.sort_order)
    const currentIndex = sortedItems.findIndex(item => item.id === itemId)
    
    if (currentIndex > 0) {
      const newItems = [ ...sortedItems ]
      // Меняем местами текущий элемент с предыдущим
      const temp = newItems[currentIndex]
      newItems[currentIndex] = newItems[currentIndex - 1]
      newItems[currentIndex - 1] = temp
      
      // Обновляем sort_order
      const updatedItems = newItems.map((item, index) => ({
        ...item,
        sort_order: index + 1
      }))
      
      setMenuItems(updatedItems)
    }
  }

  const moveItemDown = (itemId: GQL_ID) => {
    const sortedItems = [ ...menuItems ].sort((a, b) => a.sort_order - b.sort_order)
    const currentIndex = sortedItems.findIndex(item => item.id === itemId)
    
    if (currentIndex < sortedItems.length - 1) {
      const newItems = [ ...sortedItems ]
      // Меняем местами текущий элемент со следующим
      const temp = newItems[currentIndex]
      newItems[currentIndex] = newItems[currentIndex + 1]
      newItems[currentIndex + 1] = temp
      
      // Обновляем sort_order
      const updatedItems = newItems.map((item, index) => ({
        ...item,
        sort_order: index + 1
      }))
      
      setMenuItems(updatedItems)
    }
  }

  const saveSortOrder = async () => {
    if (!isEditing || !menuId) {
      alert('Сначала сохраните меню')
      return
    }

    try {
      // Сохраняем порядок сортировки для каждого элемента
      const updatePromises = menuItems.map(item => 
        updateMenuItem({
          variables: {
            id: item.id,
            input: {
              sort_order: item.sort_order
            }
          }
        })
      )

      await Promise.all(updatePromises)
      alert('Порядок сортировки сохранен')
    }
    catch (error) {
      console.error('Error saving sort order:', error)
      alert('Ошибка при сохранении порядка сортировки')
    }
  }

  if (error || menuLoadError) {
    return (
      <GraphQLCommonErrorDisplay
        title={`Ошибка ${isEditing ? 'загрузки/обновления' : 'создания'} меню`}
        error={(error || menuLoadError) as any}
      />
    )
  }

  if (isEditing && menuLoading) {
    return (
      <div className="p-4">
        <h1 className="u-heading">Загрузка меню...</h1>
        <SettingsTopMenu />
      </div>
    )
  }

  return (
    <div className="MenuEditPage">
      <h1 className="u-heading">
        {isEditing ? 'Редактирование меню' : 'Создание меню'}
      </h1>
      <SettingsTopMenu />

      {/* Навигация по вкладкам */}
      <Navbar className="mt-4">
        <Nav 
          appearance="tabs"
          activeKey={activeTab} 
          onSelect={(eventKey) => setActiveTab(eventKey as string)}
        >
          <Nav.Item eventKey="items">
            📋 Элементы меню ({menuItems.length})
          </Nav.Item>
          <Nav.Item eventKey="settings">
            ⚙️ Настройки меню
          </Nav.Item>
        </Nav>
      </Navbar>

      {/* Вкладка: Элементы меню */}
      {activeTab === 'items' && (
        <Panel shaded className="mt-6">
          {!isEditing && (
            <div className="mb-6 p-4 bg-blue-50 rounded border border-blue-200">
              <p className="text-blue-800 text-sm">
                <strong>Совет:</strong> Сначала создайте меню на вкладке "Настройки меню", 
                а затем добавьте элементы. Элементы будут сохранены вместе с меню.
              </p>
            </div>
          )}

          {isEditing && menuItems.length > 0 && (
            <div className="mb-6 p-4 bg-green-50 rounded border border-green-200">
              <p className="text-green-800 text-sm">
                <strong>Управление порядком:</strong> Используйте кнопки ▲▼ для изменения порядка элементов. 
                После изменений нажмите "Сохранить порядок" для применения изменений.
              </p>
            </div>
          )}
          
          <div className="bg-white rounded-lg border border-gray-200 p-6 mb-6">
            <FlexboxGrid justify="space-between" align="middle" className="mb-4">
              <FlexboxGrid.Item>
                <div className="flex items-center">
                  <MenuOutline style={{ fontSize: '24px', color: '#6b7280', marginRight: '12px' }} />
                  <div>
                    <h3 className="text-xl font-semibold text-gray-900 m-0">
                      Элементы меню
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Управление элементами меню и их настройками
                    </p>
                  </div>
                </div>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item>
                <div className="flex items-center gap-3">
                  {menuItems.length > 0 && (
                    <Button
                      appearance="ghost"
                      size="md"
                      onClick={saveSortOrder}
                      className="border-dashed"
                      style={{
                        borderColor: '#3498db',
                        color: '#3498db'
                      }}
                    >
                      💾 Сохранить порядок
                    </Button>
                  )}
                  <Button
                    appearance="primary"
                    size="md"
                    onClick={handleAddItem}
                    startIcon={<AddOutline />}
                    className="shadow-sm"
                    style={{
                      backgroundColor: '#3498db',
                      borderColor: '#3498db',
                      fontWeight: 500
                    }}
                  >
                    Добавить элемент
                  </Button>
                </div>
              </FlexboxGrid.Item>
            </FlexboxGrid>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden shadow-sm">
            <Table
              data={[ ...menuItems ].sort((a, b) => a.sort_order - b.sort_order)}
              autoHeight
              className="menu-items-table"
              style={{ 
                border: 'none'
              }}
            >
              <Column flexGrow={1} align="center">
                <HeaderCell style={{ padding: '16px 12px', fontWeight: 600, backgroundColor: '#f8f9fa' }}>
                  №
                </HeaderCell>
                <Cell dataKey="sort_order" style={{ padding: '16px 12px' }} />
              </Column>

              <Column flexGrow={2}>
                <HeaderCell style={{ padding: '16px 12px', fontWeight: 600, backgroundColor: '#f8f9fa' }}>
                  Внутреннее имя
                </HeaderCell>
                <Cell style={{ padding: '16px 12px' }}>
                  {(rowData: MenuItem) => (
                    <code className="bg-gray-50 px-2 py-1 rounded text-xs font-mono text-gray-700 border">
                      {rowData.internal_name}
                    </code>
                  )}
                </Cell>
              </Column>

              <Column flexGrow={2}>
                <HeaderCell style={{ padding: '16px 12px', fontWeight: 600, backgroundColor: '#f8f9fa' }}>
                  Название
                </HeaderCell>
                <Cell style={{ padding: '16px 12px' }}>
                  {(rowData: MenuItem) => (
                    <div>
                      <div className="font-medium text-gray-900">
                        {rowData.title}
                      </div>
                    </div>
                  )}
                </Cell>
              </Column>

              <Column flexGrow={2} align="center">
                <HeaderCell style={{ padding: '16px 12px', fontWeight: 600, backgroundColor: '#f8f9fa' }}>
                  Изображение
                </HeaderCell>
                <Cell>
                  {(rowData: MenuItem) => (
                    rowData.image_file_id ? (
                      <img 
                        src={rowData.image?.url} 
                        alt={rowData.title}
                        style={{ 
                          width: '32px', 
                          height: '32px', 
                          objectFit: 'cover',
                          borderRadius: '4px'
                        }}
                      />
                    ) : (
                      <span className="text-gray-400 text-sm">—</span>
                    )
                  )}
                </Cell>
              </Column>

              <Column flexGrow={2}>
                <HeaderCell style={{ padding: '16px 12px', fontWeight: 600, backgroundColor: '#f8f9fa' }}>
                  Тип ссылки
                </HeaderCell>
                <Cell style={{ padding: '16px 12px' }}>
                  {(rowData: MenuItem) => {
                    const linkType = LINK_TYPES.find(type => type.value === rowData.link_type)
                    return (
                      <Badge 
                        color="blue"
                        content={linkType?.label || rowData.link_type}
                      />
                    )
                  }}
                </Cell>
              </Column>

              <Column flexGrow={1} align="center">
                <HeaderCell style={{ padding: '16px 12px', fontWeight: 600, backgroundColor: '#f8f9fa' }}>
                  Статус
                </HeaderCell>
                <Cell style={{ padding: '16px 12px' }}>
                  {(rowData: MenuItem) => (
                    <Tag 
                      color={rowData.is_active ? 'green' : 'red'}
                    >
                      { rowData.is_active ? 'Активен' : 'Неактивен' }
                    </Tag>
                  )}
                </Cell>
              </Column>

              <Column flexGrow={1} align="center">
                <HeaderCell style={{ padding: '16px 12px', fontWeight: 600, backgroundColor: '#f8f9fa' }}>
                  Порядок
                </HeaderCell>
                <Cell style={{ padding: '16px 12px' }}>
                  {(rowData: MenuItem) => {
                    const sortedItems = [ ...menuItems ].sort((a, b) => a.sort_order - b.sort_order)
                    const currentIndex = sortedItems.findIndex(item => item.id === rowData.id)
                    const isFirst = currentIndex === 0
                    const isLast = currentIndex === sortedItems.length - 1
                    
                    return (
                      <div className="flex items-center justify-center space-x-1">
                        <IconButton
                          size="xs"
                          icon={<ChevronUpOutline />}
                          onClick={() => moveItemUp(rowData.id)}
                          disabled={isFirst}
                          appearance="subtle"
                          circle
                          style={{
                            color: isFirst ? '#ccc' : '#2ecc71',
                            backgroundColor: isFirst ? 'transparent' : 'rgba(46, 204, 113, 0.1)',
                            border: 'none',
                            width: '24px',
                            height: '24px'
                          }}
                          title="Переместить вверх"
                        />
                        <IconButton
                          size="xs"
                          icon={<ChevronDownOutline />}
                          onClick={() => moveItemDown(rowData.id)}
                          disabled={isLast}
                          appearance="subtle"
                          circle
                          style={{
                            color: isLast ? '#ccc' : '#2ecc71',
                            backgroundColor: isLast ? 'transparent' : 'rgba(46, 204, 113, 0.1)',
                            border: 'none',
                            width: '24px',
                            height: '24px'
                          }}
                          title="Переместить вниз"
                        />
                      </div>
                    )
                  }}
                </Cell>
              </Column>

              <Column flexGrow={2} align="center" fixed="right">
                <HeaderCell style={{ padding: '16px 12px', fontWeight: 600, backgroundColor: '#f8f9fa' }}>
                  Действия
                </HeaderCell>
                <Cell style={{ padding: '16px 12px' }}>
                  {(rowData: MenuItem) => (
                    <div className="flex items-center justify-center space-x-1">
                      <IconButton
                        size="xs"
                        icon={<CreateOutline />}
                        onClick={() => handleEditItem(rowData)}
                        appearance="subtle"
                        circle
                        style={{
                          color: '#3498db',
                          backgroundColor: 'rgba(52, 152, 219, 0.1)',
                          border: 'none',
                          width: '28px',
                          height: '28px'
                        }}
                        title="Редактировать"
                      />
                      <IconButton
                        size="xs"
                        icon={<TrashOutline />}
                        onClick={() => handleDeleteItem(rowData.id)}
                        appearance="subtle"
                        circle
                        style={{
                          color: '#e74c3c',
                          backgroundColor: 'rgba(231, 76, 60, 0.1)',
                          border: 'none',
                          width: '28px',
                          height: '28px'
                        }}
                        title="Удалить"
                      />
                    </div>
                  )}
                </Cell>
              </Column>
            </Table>
          </div>
        </Panel>
      )}

      {/* Вкладка: Настройки меню */}
      {activeTab === 'settings' && (
        <Panel shaded className="mt-4">
          <Form layout="horizontal" formValue={formData}>
            <Form.Group>
              <Form.ControlLabel>Название меню</Form.ControlLabel>
              <Input
                name="name"
                value={formData.name}
                onChange={(value: string) => updateFormData('name', value)}
                placeholder="Введите название меню"
              />
              <Form.HelpText>
                Внутреннее название для использования в админке
              </Form.HelpText>
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Код меню</Form.ControlLabel>
              <Input
                name="code"
                value={formData.code}
                onChange={(value: string) => updateFormData('code', value)}
                placeholder="main-menu"
              />
              <Form.HelpText>
                Уникальный код для использования во фронтенде
              </Form.HelpText>
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Тип меню</Form.ControlLabel>
              <SelectPicker
                name="type"
                data={MENU_TYPES}
                value={formData.type}
                onChange={(value) => updateFormData('type', value)}
                cleanable={false}
                searchable={false}
                block
              />
              <Form.HelpText>
                Тип определяет способ отображения меню
              </Form.HelpText>
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Активность</Form.ControlLabel>
              <Toggle
                checked={formData.is_active}
                onChange={(checked) => updateFormData('is_active', checked)}
              />
              <Form.HelpText>
                Неактивные меню не отображаются в клиентском приложении
              </Form.HelpText>
            </Form.Group>
          </Form>
        </Panel>
      )}

      {/* Кнопки сохранения - показываем всегда внизу */}
      <Panel className="mt-4">
        <ButtonToolbar>
          <Button
            appearance="primary"
            loading={loading}
            onClick={handleSubmit}
          >
            {isEditing ? 'Обновить меню' : 'Создать меню'}
          </Button>
          <Button
            appearance="subtle"
            onClick={handleCancel}
            disabled={loading}
          >
            Отмена
          </Button>
        </ButtonToolbar>
      </Panel>

      {/* Модальное окно для создания/редактирования элемента меню */}
      <Modal 
        open={showItemModal} 
        onClose={() => setShowItemModal(false)}
        size="md"
      >
        <Modal.Header>
          <Modal.Title>
            {editingItem ? 'Редактирование элемента меню' : 'Создание элемента меню'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form layout="vertical" formValue={itemFormData}>
            <Form.Group>
              <Form.ControlLabel>Внутреннее имя</Form.ControlLabel>
              <Input
                value={itemFormData.internal_name}
                onChange={(value: string) => updateItemFormData('internal_name', value)}
                placeholder="catalog"
              />
              <Form.HelpText>
                Служебное имя для идентификации в коде
              </Form.HelpText>
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Название для пользователей</Form.ControlLabel>
              <Input
                value={itemFormData.title}
                onChange={(value: string) => updateItemFormData('title', value)}
                placeholder="Каталог"
              />
              <Form.HelpText>
                Название, которое увидят пользователи
              </Form.HelpText>
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Изображение элемента меню</Form.ControlLabel>
              {itemFormData.image_url ? (
                <DropzonePreview
                  url={itemFormData.image_url}
                  onDelete={handleUnlinkMenuItemImage}
                />
              ) : (
                <DropzoneUploader
                  name={UPLOAD_NAME_MENU_ITEM_IMAGE}
                  action={API_URL_UPLOAD_MENU_ITEM_IMAGE}
                  onUploaded={handleMenuItemImageUploaded}
                  multiple={false}
                />
              )}
              <Form.HelpText>
                Изображение для отображения (jpg, png)
              </Form.HelpText>
            </Form.Group>

            <FlexboxGrid>
              <FlexboxGrid.Item colspan={12}>
                <Form.Group>
                  <Form.ControlLabel>Цвет текста</Form.ControlLabel>
                  <Input
                    value={itemFormData.text_color}
                    onChange={(value: string) => updateItemFormData('text_color', value)}
                    placeholder="#333333"
                  />
                </Form.Group>
              </FlexboxGrid.Item>
              <FlexboxGrid.Item colspan={12}>
                <Form.Group>
                  <Form.ControlLabel>Жирный текст</Form.ControlLabel>
                  <FormCheckbox.Control
                    name='bold' 
                    placeholder='Жирный текст'
                    onChange={(value: boolean) => updateItemFormData('bold', value)}
                  />
                </Form.Group>
              </FlexboxGrid.Item>
            </FlexboxGrid>

            <Form.Group>
              <Form.ControlLabel>Тип ссылки</Form.ControlLabel>
              <SelectPicker
                data={LINK_TYPES}
                value={itemFormData.link_type}
                onChange={(value) => updateItemFormData('link_type', value)}
                cleanable={false}
                searchable={false}
                block
              />
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>URL ссылки</Form.ControlLabel>
              <Input
                value={itemFormData.link_url}
                onChange={(value: string) => updateItemFormData('link_url', value)}
                placeholder="/catalog"
              />
              <Form.HelpText>
                Относительный или абсолютный URL
              </Form.HelpText>
            </Form.Group>

            <Divider />
            
            <Form.Group>
              <Form.ControlLabel>Переводы</Form.ControlLabel>
              <SelectLocale
                selectedLanguage={selectedItemLanguage}
                onSelect={setSelectedItemLanguage}
                onAdd={handleAddItemLanguage}
                onRemove={handleRemoveItemLanguage}
                languages={itemLanguages}
              />
              {selectedItemLanguage && (
                <LocalizedForm
                  name='title'
                  label='Название'
                  selectedLanguage={selectedItemLanguage}
                  value={selectedItemTranslationTitle}
                  onChange={handleItemTranslationChange}
                />
              )}
              <Form.HelpText>
                Добавьте переводы названия элемента для разных языков
              </Form.HelpText>
            </Form.Group>

            <Form.Group>
              <Form.ControlLabel>Активность</Form.ControlLabel>
              <Toggle
                checked={itemFormData.is_active}
                onChange={(checked) => updateItemFormData('is_active', checked)}
              />
              <Form.HelpText>
                Неактивные элементы не отображаются в меню
              </Form.HelpText>
            </Form.Group>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={handleSaveItem} appearance="primary">
            {editingItem ? 'Обновить' : 'Создать'}
          </Button>
          <Button onClick={() => setShowItemModal(false)} appearance="subtle">
            Отмена
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  )
}
